const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const normalizeCas = (value) => normalizeText(value).replace(/\s/g, "");

const splitTerms = (value) =>
  String(value || "")
    .split(/\s*,\s*/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const ruleTerms = (rule = {}) =>
  [
    rule.substanceName,
    rule.inciName,
    ...(rule.synonyms || []),
    ...(rule.casNumbers || []),
    ...(rule.ecNumbers || []),
  ]
    .flatMap(splitTerms)
    .map(normalizeText)
    .filter(Boolean);

const changeTerms = (change = {}) =>
  [
    change.entityKey,
    ...(change.relatedNames || []),
    ...ruleTerms(change.currentRule),
    ...ruleTerms(change.previousRule),
  ]
    .map(normalizeText)
    .filter(Boolean);

const collectProductIngredientTerms = (product) => {
  const activeFormula =
    product.compliance?.activeFormula ||
    product.formulaVersions?.find((formula) => formula.id === product.currentFormulaId) ||
    product.formulaVersions?.[0];

  if (!activeFormula) {
    return [];
  }

  const ifraResults = activeFormula.ifraResults || [];
  if (ifraResults.length) {
    return ifraResults.flatMap((item) =>
      [
        item.ingredient_name,
        item.cas_number && item.cas_number !== "N/A" ? item.cas_number : "",
      ]
        .map(normalizeText)
        .filter(Boolean)
    );
  }

  return String(activeFormula.inputText || "")
    .split(/\r?\n/)
    .map((line) => normalizeText(line.split(/[:,-]/)[0]))
    .filter(Boolean);
};

const collectProductIngredientRows = (product) => {
  const activeFormula =
    product.compliance?.activeFormula ||
    product.formulaVersions?.find((formula) => formula.id === product.currentFormulaId) ||
    product.formulaVersions?.[0];

  const ifraResults = activeFormula?.ifraResults || [];
  return ifraResults.map((item) => ({
    name: item.ingredient_name,
    casNumber: item.cas_number && item.cas_number !== "N/A" ? item.cas_number : "",
    formulaPercentage: Number.isFinite(Number(item.formula_percentage))
      ? Number(item.formula_percentage)
      : null,
    concentrationVerified: item.concentration_verified === true,
    terms: [item.ingredient_name, item.cas_number]
      .map(normalizeText)
      .filter((term) => term && term !== "n/a"),
  }));
};

const termsMatch = (leftTerms, rightTerms) => {
  const leftSet = new Set(leftTerms);
  return rightTerms.some((term) => {
    if (leftSet.has(term)) {
      return true;
    }

    const normalizedCas = normalizeCas(term);
    return normalizedCas && leftSet.has(normalizedCas);
  });
};

const classifyAnnexImpact = ({ change, ingredient }) => {
  if (change.changeType === "annex_rule_removed") {
    return { level: "review", status: "Review", action: "Confirm removal with legal before changing release status." };
  }

  if (change.ruleType === "prohibited" || change.currentRule?.ruleType === "prohibited") {
    return { level: "critical", status: "Blocked", action: "Remove prohibited substance or block EU release." };
  }

  const maxPercentage = Number(change.currentRule?.maxPercentage);
  if (Number.isFinite(maxPercentage) && ingredient?.formulaPercentage !== null) {
    if (ingredient.formulaPercentage > maxPercentage) {
      const overage = ((ingredient.formulaPercentage - maxPercentage) / maxPercentage) * 100;
      return {
        level: "critical",
        status: "Blocked",
        action: `Reduce below ${maxPercentage}% EU limit; current is ${ingredient.formulaPercentage}% (${overage.toFixed(1)}% over).`,
      };
    }

    return {
      level: change.impactLevel || "high",
      status: "Review",
      action: `Verify formula remains below ${maxPercentage}% EU limit; current is ${ingredient.formulaPercentage}%.`,
    };
  }

  return {
    level: change.impactLevel || "high",
    status: "Review",
    action: "Review updated EU Annex rule against formula concentration and label/PIF artifacts.",
  };
};

export const buildRegulatoryTrackerSnapshot = ({
  products = [],
  regulatorySources = [],
  regulatoryIngredients = [],
  regulatoryChanges = [],
}) => {
  const productIngredientMap = products.map((product) => ({
    product,
    ingredients: collectProductIngredientTerms(product),
    ingredientRows: collectProductIngredientRows(product),
  }));

  const impacts = regulatoryChanges.flatMap((change) => {
    const entityTerms = changeTerms(change);

    if (!entityTerms.length) {
      return [];
    }

    return productIngredientMap
      .map(({ product, ingredients, ingredientRows }) => {
        if (!termsMatch(ingredients, entityTerms)) {
          return null;
        }

        const matchedIngredient =
          ingredientRows.find((ingredient) => termsMatch(ingredient.terms, entityTerms)) || null;
        const impact = classifyAnnexImpact({ change, ingredient: matchedIngredient });

        return {
          id: `${change.id}-${product.id}`,
          changeId: change.id,
          productId: product.id,
          productName: product.name,
          ingredientName: matchedIngredient?.name || change.entityKey,
          casNumber: matchedIngredient?.casNumber || "",
          formulaPercentage: matchedIngredient?.formulaPercentage,
          concentrationVerified: matchedIngredient?.concentrationVerified === true,
          impactLevel: impact.level,
          message: `${product.name} uses ${matchedIngredient?.name || change.entityKey}. ${change.summary}`,
          action: impact.action,
          jurisdiction: "EU",
          status: impact.status,
          changeType: change.changeType,
          oldValue: change.oldValue || "",
          newValue: change.newValue || "",
          sourceUrl: change.sourceUrl || "",
        };
      })
      .filter(Boolean);
  });

  const sourceHealth = regulatorySources.map((source) => ({
    ...source,
    status:
      source.lastSyncStatus ||
      (source.lastSyncedAt ? "Healthy" : "Pending"),
  }));

  return {
    sources: regulatorySources,
    sourceHealth,
    ingredients: regulatoryIngredients,
    changes: regulatoryChanges,
    impacts,
  };
};
